import { useEffect, useMemo, useState } from 'react';
import { Button, FORM_CONTROL_CLASS, FORM_LABEL_CLASS, FORM_TEXTAREA_CLASS, Modal } from '@safetyhub/ui';
import { PERMISSION_GROUP_LABELS, groupPermissions } from '@safetyhub/shared';
import { MODAL_LAYER } from '../../../components/modalLayers';
import type { CreateCompanyRoleDto, PermissionDefinitionView } from '../../../lib/rbac';

interface CreateRoleModalProps {
  open: boolean;
  permissions: PermissionDefinitionView[];
  isSubmitting: boolean;
  overlayZClassName?: string;
  onSubmit: (payload: CreateCompanyRoleDto) => Promise<void>;
  onClose: () => void;
}

/**
 * 새 조직 역할(scope COMPANY) — 이름·설명·권한 체크. 조직 역할은 `orgAdjustable` 키만 가질 수 있어(결정 §3)
 * 잠긴 키는 노출하지 않는다. 생성 모드라 dirty 게이트 비대상.
 */
export function CreateRoleModal({
  open,
  permissions,
  isSubmitting,
  overlayZClassName = MODAL_LAYER.root,
  onSubmit,
  onClose,
}: CreateRoleModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!open) return;
    setName('');
    setDescription('');
    setSelected(new Set());
  }, [open]);

  const adjustableGroups = useMemo(
    () => groupPermissions(permissions.filter((p) => p.orgAdjustable)),
    [permissions],
  );

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const canSubmit = name.trim() !== '' && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const payload: CreateCompanyRoleDto = {
      name: name.trim(),
      permissionKeys: Array.from(selected),
    };
    if (description.trim()) payload.description = description.trim();
    await onSubmit(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="새 역할"
      size="lg"
      overlayZClassName={overlayZClassName}
      closeLocked={isSubmitting}
      footer={
        <>
          <Button type="button" onClick={onClose} disabled={isSubmitting}>
            취소
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting ? '만드는 중...' : '만들기'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <div>
          <label htmlFor="new-role-name" className={FORM_LABEL_CLASS}>
            역할 이름 <span className="text-rose-500">*</span>
          </label>
          <input
            id="new-role-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={FORM_CONTROL_CLASS}
            placeholder="예: 현장 점검원"
            disabled={isSubmitting}
            autoFocus
          />
        </div>
        <div>
          <label htmlFor="new-role-description" className={FORM_LABEL_CLASS}>
            설명 (선택)
          </label>
          <textarea
            id="new-role-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={FORM_TEXTAREA_CLASS}
            rows={2}
            disabled={isSubmitting}
          />
        </div>
        <fieldset>
          <legend className={FORM_LABEL_CLASS}>권한</legend>
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {adjustableGroups.map(({ group, items }) => (
              <div key={group}>
                <p className="text-xs font-semibold text-ink-500 mb-1">{PERMISSION_GROUP_LABELS[group] ?? group}</p>
                <ul className="space-y-1">
                  {items.map((p) => (
                    <li key={p.key}>
                      <label className="flex items-start gap-2 text-sm text-ink-700">
                        <input
                          type="checkbox"
                          checked={selected.has(p.key)}
                          onChange={() => toggle(p.key)}
                          className="mt-0.5 h-4 w-4 rounded border-line text-brand-600"
                          disabled={isSubmitting}
                        />
                        <span>
                          <span className="font-medium text-ink-900">{p.label}</span>
                          {p.description && <span className="block text-xs text-ink-500">{p.description}</span>}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </fieldset>
      </form>
    </Modal>
  );
}
